// Vollea — Super-admin Club approval queue
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBack, IoCheckmarkCircle, IoTimeOutline, IoCheckmark, IoBusinessOutline } from "react-icons/io5";
import { Card, Pill, Empty } from "../../components/ui-vollea";
import { api } from "../../lib/api";
import { C, SP, R } from "../../lib/theme";

export default function AdminClubs() {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setClubs(await api.get("/clubs") || []); }
    catch { setClubs([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (cid) => {
    await api.post(`/clubs/${cid}/approve`);
    load();
  };

  const pending = clubs.filter((c) => !c.approved);
  const approved = clubs.filter((c) => c.approved);
  const view = tab === "pending" ? pending : tab === "approved" ? approved : clubs;

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: `${SP.lg}px ${SP.lg}px 90px` }}>
      <button onClick={() => navigate("/admin")} data-testid="admin-clubs-back" style={{ color: C.text, marginBottom: SP.md }}>
        <IoChevronBack size={26} />
      </button>
      <h1 style={{ color: C.text, fontWeight: 700, fontSize: 28, margin: 0, letterSpacing: 0.5 }}>Clubs</h1>
      <div style={{ display: "flex", alignItems: "center", gap: SP.sm, marginTop: 8 }}>
        <span data-testid="admin-clubs-pending-badge" style={{ background: "rgba(245,158,11,0.15)", color: C.warning, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: R.pill, letterSpacing: 1, textTransform: "uppercase" }}>
          {pending.length} pending
        </span>
        <span style={{ color: C.textDim, fontSize: 13 }}>· {clubs.length} total</span>
      </div>

      <div style={{ display: "flex", gap: SP.sm, marginTop: SP.lg, marginBottom: SP.md }}>
        <Pill label={`Pending (${pending.length})`} active={tab === "pending"} onClick={() => setTab("pending")} testid="admin-clubs-tab-pending" />
        <Pill label={`Approved (${approved.length})`} active={tab === "approved"} onClick={() => setTab("approved")} testid="admin-clubs-tab-approved" />
        <Pill label={`All (${clubs.length})`} active={tab === "all"} onClick={() => setTab("all")} testid="admin-clubs-tab-all" />
      </div>

      <div data-testid="admin-clubs-list" style={{ display: "flex", flexDirection: "column", gap: SP.sm }}>
        {loading ? (
          <div style={{ textAlign: "center", color: C.lime, padding: SP.xl }}>Loading…</div>
        ) : view.length === 0 ? (
          <Empty
            icon={<IoBusinessOutline size={36} color={C.textDim} />}
            text={tab === "pending" ? "No clubs awaiting approval. Great job staying on top of it." : "No clubs in this view."}
            testid="admin-clubs-empty"
          />
        ) : view.map((c) => (
          <Card key={c.club_id} testid={`admin-club-row-${c.club_id}`}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: SP.md }}>
              <div style={{ width: 44, height: 44, borderRadius: R.md, background: C.limeTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <IoBusinessOutline size={20} color={C.lime} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: SP.sm, flexWrap: "wrap" }}>
                  <span style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{c.name}</span>
                  {c.approved ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.limeTint, color: C.lime, fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: R.pill, letterSpacing: 1, textTransform: "uppercase" }}>
                      <IoCheckmarkCircle size={10} /> Approved
                    </span>
                  ) : (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(245,158,11,0.15)", color: C.warning, fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: R.pill, letterSpacing: 1, textTransform: "uppercase" }}>
                      <IoTimeOutline size={10} /> Pending
                    </span>
                  )}
                </div>
                <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>{c.location}</div>
                {c.description ? <div style={{ color: C.textDim, fontSize: 12, marginTop: 4 }}>{c.description}</div> : null}
                <div style={{ color: C.textDim, fontSize: 11, marginTop: 6, fontFamily: "monospace", letterSpacing: 2 }}>CODE {c.code}</div>
              </div>
              {!c.approved && (
                <button onClick={() => approve(c.club_id)} data-testid={`admin-club-approve-${c.club_id}`} style={{ padding: "8px 12px", borderRadius: R.sm, background: C.lime, color: C.onLime, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, letterSpacing: 1, textTransform: "uppercase" }}>
                  <IoCheckmark size={14} /> Approve
                </button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
